import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import TextareaAutosize from 'react-autosize-textarea';
import ModalTrigger from '../../common/ModalTrigger';
import NotInvoicingModal from '../../NotInvoicingModal';
import ProjectNameSummaryModal from '../../ProjectNameSummaryModal';
import { putRequest, postRequest } from '../../../requestUtils';
import { formatCurrency, formatCurrencyPlain } from '../../../numberUtils';
import InfoTooltip from '../../common/InfoTooltip';
import ActionDropdown from '../../common/ActionDropdown/ActionDropdown';
import dollarSign from '../../../../assets/images/icons/dollar-sign.png';
import chevronDown from '../../../../assets/images/icons/chevronDownWhite.svg';
import './InvoiceHeader.scss';

const dateDiffInDays = (a, b) => {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.ceil((utc2 - utc1) / (1000 * 60 * 60 * 24));
};

const numericKeyFilter = (e) => {
  const re = /[0-9]/;
  const allowed = [8, 46, 37, 38, 39, 40];
  if (allowed.includes(e.keyCode)) {
    return;
  }
  if (!re.test(e.key)) {
    e.preventDefault();
  }
};

class InvoiceHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: props.status,
      statusDisplay: props.statusDisplay,
      memo: props.memo,
      projectedHours: props.projectedHours,
      submissionRequested: false,
      requestSubmissionMessage: null,
      isReadingMore: false,
      showReadMore: false,
      showErrorMsg: false,
      consultantPayoutOverride: formatCurrencyPlain(props.consultantPayoutOverride),
      grossRevenueOverride: formatCurrencyPlain(props.grossRevenueOverride),
      overrideReason: props.overrideReason,
      overrideSubmitted: (props.consultantPayoutOverride || props.grossRevenueOverride),
      overrideErrors: [],
      showAmountOverrideModal: false,
      forceShowModalKey: 0,
    };

    this.readMore = this.readMore.bind(this);
    this.errorMsg = this.errorMsg.bind(this);
    this.updateMemo = this.updateMemo.bind(this);
    this.handleUpdateMemo = debounce(this.handleUpdateMemo.bind(this), 300);
    this.handleRevertToDraft = this.handleRevertToDraft.bind(this);
    this.updateProjectedHours = this.updateProjectedHours.bind(this);
    this.handleSubmitOverrides = this.handleSubmitOverrides.bind(this);
    this.handleUpdateProjectedHours = debounce(this.handleUpdateProjectedHours.bind(this));
    this.updateRequestSubmissionMessage = this.updateRequestSubmissionMessage.bind(this);
    this.handleRequestSubmissionSubmit = this.handleRequestSubmissionSubmit.bind(this);
    this.renderRequestSubmission = this.renderRequestSubmission.bind(this);
    this.renderSubmitTimeSheet = this.renderSubmitTimeSheet.bind(this);
    this.toggleAmountOverrideModal = this.toggleAmountOverrideModal.bind(this);
  }

  componentDidMount() {
    this.setShowReadMore();
  }

  setShowReadMore() {
    // Added 1 to clientHeight because in Safari 12.1 there is a rounding error leading
    // scrollHeight to be bigger than the clientHeight by 1 and so showReadMore is true
    this.setState({
      showReadMore: this.memoElement
        && this.memoElement.scrollHeight > this.memoElement.clientHeight + 1,
    });
  }

  readMore() {
    this.setState({ isReadingMore: !this.state.isReadingMore });
  }

  errorMsg(value) {
    this.setState({ showErrorMsg: value });
  }

  updateMemo(value) {
    this.setState({ memo: value });
    this.handleUpdateMemo();
  }

  handleUpdateMemo() {
    const { csrfToken, id } = this.props;
    const { memo } = this.state;
    putRequest(`/invoices/${id}`, { invoice: { memo } }, csrfToken);
  }

  handleRevertToDraft() {
    const { csrfToken, id } = this.props;
    putRequest(
      `/invoices/${id}/draft`,
      {},
      csrfToken,
    ).then(({ data }) => {
      this.setState({
        statusDisplay: data.status_display,
        status: data.status,
      });
    });
  }

  updateProjectedHours(value) {
    this.errorMsg(false);
    this.setState({ projectedHours: value });
    this.handleUpdateProjectedHours();
  }

  handleUpdateProjectedHours() {
    const { csrfToken, id } = this.props;
    const { projectedHours } = this.state;
    putRequest(
      `/invoices/${id}`,
      { projected_hours_by_consultants: projectedHours },
      csrfToken,
    );
  }

  updateRequestSubmissionMessage(value) {
    this.setState({ requestSubmissionMessage: value });
  }

  handleRequestSubmissionSubmit() {
    const { id, csrfToken } = this.props;
    const { requestSubmissionMessage } = this.state;

    return postRequest(
      `/invoices/${id}/remind_to_submit`,
      { message: requestSubmissionMessage },
      csrfToken,
    ).then(() => {
      this.setState({ submissionRequested: true });
    });
  }

  handleSubmitOverrides() {
    const { id, csrfToken } = this.props;
    const {
      grossRevenueOverride, consultantPayoutOverride,
      overrideReason, forceShowModalKey,
    } = this.state;

    postRequest(
      `/invoices/${id}/add_override`,
      {
        invoice_override: {
          gross_revenue: grossRevenueOverride
            ? grossRevenueOverride.replace(/,/, '') * 100 : null,
          consultant_payout: consultantPayoutOverride
            ? consultantPayoutOverride.replace(/,/, '') * 100 : null,
          reason_for_change: overrideReason,
        },
      },
      csrfToken,
    ).then(() => {
      this.setState({
        forceShowModalKey: forceShowModalKey + 1,
        showAmountOverrideModal: false,
        overrideSuccess: true,
        overrideErrors: [],
        overrideSubmitted: true,
      });
    }).catch((err) => {
      this.setState({ overrideSuccess: false, overrideErrors: err.errors });
    });
  }

  overridesValid() {
    const { grossRevenueOverride, consultantPayoutOverride, overrideReason } = this.state;

    return overrideReason && (grossRevenueOverride || consultantPayoutOverride);
  }

  toggleAmountOverrideModal() {
    const { forceShowModalKey } = this.state;

    this.setState(
      {
        showAmountOverrideModal: true,
        forceShowModalKey: forceShowModalKey + 1,
      },
    );
  }

  renderInvoiceState() {
    const {
      statusDisplay, statusChangeDate,
      editsRequestedBy, editsRequestedOn, approvedBy, approvedOn,
    } = this.props;
    const { status } = this.state;
    if (status === 'draft') {
      return this.renderDaysLeftToSubmit('timesheet');
    } if (status === 'edits_requested' && editsRequestedBy && editsRequestedOn) {
      return (
        <div>
          <div>
            Edits requested by
            {' '}
            {editsRequestedBy}
            {' '}
            on
            {' '}
            {window.moment(editsRequestedOn).format('LL')}
          </div>
          {this.renderDaysLeftToSubmit('changes')}
        </div>
      );
    } if (status === 'approved_by_rep' && approvedBy && approvedOn) {
      return `Approved by ${approvedBy} - ${window.moment(approvedOn).format('LL')}`;
    }
    return `${statusDisplay} on ${statusChangeDate}`;
  }

  renderDaysLeftToSubmit(item) {
    const { consultantDueDate } = this.props;

    const daysLate = dateDiffInDays(new Date(consultantDueDate.replace(/-/g, '/')), new Date());
    if (daysLate > 0) {
      return (
        <span className="late-warning">
          <i className="far fa-clock me-2" />
          {daysLate}
          {' '}
          day
          {Math.abs(daysLate) > 1 ? 's' : ''}
          {' '}
          late. Please submit your
          {item}
          {' '}
          ASAP!
        </span>
      );
    }
    return (
      <span>
        <i className="far fa-clock me-2" />
        <strong>
          {daysLate * -1}
          {' '}
          day
          {Math.abs(daysLate) > 1 ? 's' : ''}
          {' '}
          left
        </strong>
        {' '}
        to submit
        {item}
      </span>
    );
  }

  renderAmountOverrideWarning() {
    const { isRep, overrideDate } = this.props;
    const { consultantPayoutOverride, grossRevenueOverride, overrideSubmitted } = this.state;

    if (!isRep || !overrideSubmitted
      || !(consultantPayoutOverride || grossRevenueOverride)
    ) return null;
    return (
      <div className="mt-2 body-text-2">
        <div className="invoice-override-warning">
          <i className="fa fa-exclamation-triangle mr-8" />
          Overrides added on
          {' '}
          {overrideDate}
          .
          {' '}
          Total invoice value no longer matches the actual invoice.
          {' '}
          <a role="link" tabIndex={0} className="override-details-link" onClick={this.toggleAmountOverrideModal}>See details</a>
        </div>
      </div>
    );
  }

  renderHourLimitWarning() {
    const {
      showHourWarning, hourLimit, totalHours, totalMinutes,
    } = this.props;
    let addedTotalHours = totalHours;
    if (totalMinutes >= 60) {
      addedTotalHours += parseInt(totalMinutes / 60, 10);
    }

    if (showHourWarning && addedTotalHours > hourLimit) {
      return (
        <div className="hour-limit-warning">
          <i className="fa fa-exclamation-triangle mr-8" />
          You are above your
          {' '}
          {hourLimit}
          {' '}
          hour limit this month
          but you may still submit your timesheet.
        </div>
      );
    }
    return false;
  }

  renderSubmittedText() {
    const { employerApprovalDueDate } = this.props;
    const today = new Date();
    if (today > new Date(employerApprovalDueDate)) {
      return 'Approval window about to expire. Approve now.';
    }

    const formattedDueDate = window.moment(employerApprovalDueDate).format('MMMM D');

    return `Approve this timesheet by ${formattedDueDate}`;
  }

  renderDueDate() {
    const {
      isProjectTeamMember, isRep, updatedAt, employerApprovalDueDate,
    } = this.props;
    const { status } = this.state;
    if (isProjectTeamMember && status === 'submitted') {
      return (
        <span className="submitted-display-alert body-text-2 normal-weight">
          <i className="fa fa-exclamation-triangle me-2" />
          {this.renderSubmittedText()}
        </span>
      );
    } if (isRep && status === 'submitted') {
      return (
        <span className="submitted-display body-text-2 normal-weight">
          Submitted
          {' '}
          {window.moment(updatedAt).format('YYYY-MM-DD')}
          . Employer must approve by
          {' '}
          {employerApprovalDueDate}
        </span>
      );
    }
    return null;
  }

  renderRequestSubmission(triggerType = 'link') {
    const { consultantName } = this.props;
    const { requestSubmissionMessage } = this.state;
    return (
      <ModalTrigger
        triggerText="Request Submission"
        triggerType={triggerType}
        buttonStyles={`${triggerType === 'button' && 'primary '} primary-link`}
        submitButtonText="Yes, Request Submission"
        cancelButtonText="Cancel"
        handleSubmit={this.handleRequestSubmissionSubmit}
        submitDisabled={!requestSubmissionMessage}
        idKey="request-submission-invoice"
        closeAfterSubmit
        modalSize="lg"
      >
        <div className="request-submission-invoice-modal style-guide p-3">
          <h2 className="mb-3">Requesting Submission</h2>
          <TextareaAutosize
            name="request-submission-message"
            value={requestSubmissionMessage}
            className="request-submission-message"
            placeholder="Add a message for the consultant here"
            rows={4}
            onChange={(e) => this.updateRequestSubmissionMessage(e.target.value)}
          />
          <p className="body-text-2 mb-3">
            Required. This message will be sent directly to
            {' '}
            {consultantName}
            &nbsp;to provide context around this request.
          </p>
        </div>
      </ModalTrigger>
    );
  }

  renderSubmitTimeSheet(triggerType = 'button') {
    const {
      id, month, year, company,
    } = this.props;

    return (
      <ModalTrigger
        triggerText="Submit Timesheet"
        triggerType={triggerType}
        cancelButtonText="No, Don't submit yet"
        idKey="submit-invoice"
        buttonStyles="primary"
        submitButtonNode={(
          <a
            href={`/invoices/${id}/submit_to_clora`}
            data-method="put"
            id="submit-invoice"
            className="btn btn-primary"
            role="button"
          >
            Yes, Submit to employer
          </a>
        )}
        closeAfterSubmit
        modalSize="lg"
      >
        <div className="approve-invoice-modal style-guide">
          <h2 className="mb-3">
            Do you want to submit your
            {' '}
            {month}
            {' '}
            {year}
            {' '}
            timesheet to
            {' '}
            {company}
            ?
          </h2>
          <p className="body-text-2 mb-3">
            You cannot modify your timesheet after submitting it to the employer.
          </p>
        </div>
      </ModalTrigger>
    );
  }

  renderRetractButton() {
    const { id } = this.props;

    return (
      <a
        href={`/invoices/${id}/edit`}
        className="invoice-request-edit-redirect"
        id="retract-and-edit-button"
        tabIndex={0}
      >
        Retract and Request Edits
      </a>
    );
  }

  renderOverridesModal() {
    const {
      isRep, company, month, year, consultantName, projectName, totals,
    } = this.props;
    const {
      grossRevenueOverride, consultantPayoutOverride, overrideReason,
      overrideErrors, overrideSuccess, showAmountOverrideModal, forceShowModalKey,
    } = this.state;

    if (!isRep) return null;

    const total = totals.totalWithoutExpenses.replace('$', '');
    const adjustedTotal = totals.adjustedTotalWithoutExpenses.replace('$', '');

    return (
      <ModalTrigger
        triggerText="Add Overrides"
        cancelButtonText="Cancel"
        idKey="add-overrides"
        buttonStyles="add-overrides-button"
        showModal={showAmountOverrideModal}
        key={forceShowModalKey}
        submitButtonNode={(
          <a
            role="button"
            tabIndex={0}
            disabled={!this.overridesValid()}
            onClick={this.overridesValid() ? this.handleSubmitOverrides : () => {}}
            id="add-invoice-override-confirm"
            className="btn btn-primary"
          >
            Save
          </a>
        )}
      >
        <h1>Add Overrides</h1>
        <div className="success-text">{ overrideSuccess ? 'Override submitted.' : '' }</div>
        <div className="body-text-2">
          Overrides will be used as the final gross revenue in various finance and investor reports.
          {' '}
          Only add overrides when you’re certain that Timesheet gross amount is inaccurate for the
          {' '}
          selected month.
        </div>
        <div className="add-invoice-override-info mt-3">
          <ul className="override-info-item">
            <li>
              Client:
              <strong>{company}</strong>
            </li>
            <li>
              Project:
              <strong>{projectName}</strong>
            </li>
            <li>
              Consultant Name:
              <strong>{consultantName}</strong>
            </li>
            <li>
              Timesheet Month:
              <strong>
                {month}
                {' '}
                {year}
              </strong>
            </li>
          </ul>
        </div>
        <div className="revenue-override mt-3">
          <div className="row revenue-section  mb-3 ">
            <div className="col-sm-5 original-gross-revenue">
              <div className="revenue-override-heading">
                Gross Revenue per Timesheet (excluding Expenses)
              </div>
              <div className="original-revenue">
                <img className="dollar-sign pe-3" src={dollarSign} alt="dollar sign" />
                <span className="amount">{ adjustedTotal }</span>
              </div>
            </div>
            <div className="col-sm-2 arrow">
              ⭢
            </div>
            <div className="col-sm-5 gross-revenue-override">
              <div className="revenue-override-heading">
                Actual Gross Revenue ($ billed to client excluding Expenses)
              </div>
              <div className="revenue-override row">
                <div className="col-sm-2">
                  <img className="dollar-sign pe-3" src={dollarSign} alt="dollar sign" />
                </div>
                <div className="col-sm-10 ps-0">
                  <input
                    type="text"
                    className="override"
                    name="gross_revenue_override"
                    value={grossRevenueOverride}
                    onChange={(e) => this.setState({ grossRevenueOverride: e.target.value })}
                    onBlur={(e) => this.setState({
                      grossRevenueOverride: formatCurrencyPlain(e.target.value),
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row revenue-section">
            <div className="col-sm-5 original-net-revenue">
              <div className="revenue-override-heading">
                Consultant Payout per Timesheet (excluding Expenses)
              </div>
              <div className="original-revenue">
                <img className="dollar-sign pe-3" src={dollarSign} alt="dollar sign" />
                <span className="amount">{ total }</span>
              </div>
            </div>
            <div className="col-sm-2 arrow">
              ⭢
            </div>
            <div className="col-sm-5 net-revenue-override">
              <div className="revenue-override-heading">
                Actual Consultant Payout (excluding Expenses)
              </div>
              <div className="revenue-override row">
                <div className="col-sm-2 pe-3">
                  <img className="dollar-sign" src={dollarSign} alt="dollar sign" />
                </div>
                <div className="col-sm-10 ps-0">
                  <input
                    type="text"
                    className="override"
                    name="consultant_payout_override"
                    value={consultantPayoutOverride}
                    onChange={(e) => this.setState({ consultantPayoutOverride: e.target.value })}
                    onBlur={(e) => this.setState({
                      consultantPayoutOverride: formatCurrencyPlain(e.target.value),
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="revenue-section">
            <div className="mt-2">
              Reason for change
            </div>
            <textarea
              name="reason_for_change"
              onChange={(e) => { this.setState({ overrideReason: e.target.value }); }}
            >
              { overrideReason }
            </textarea>
          </div>
        </div>
        <div className="error-text">{ overrideErrors.map((err) => <li>{err}</li>) }</div>
      </ModalTrigger>
    );
  }

  renderPushToQuickbooks() {
    const {
      id, hasQuickbooksCustomerId, editCompanyUrl, company,
    } = this.props;

    return (
      <ModalTrigger
        triggerText="Push to Quickbooks"
        cancelButtonText="No, Don't Push to QBO"
        idKey="push-invoice-to-quickbooks"
        buttonStyles="primary a"
        modalSize="md"
        submitButtonNode={(
          <a
            href={`/invoices/${id}/quickbooks`}
            data-method="post"
            id="push-invoice-to-quickbooks-confirm"
            className="btn btn-primary"
          >
            Yes, Push to Quickbooks
          </a>
        )}
      >
        <div className="push-invoice-to-quickbooks-modal style-guide">
          <h2 className="mb-3 center">Are you sure you want to push to quickbooks?</h2>
        </div>
        {!hasQuickbooksCustomerId
          && (
          <div className="mb-3 warning">
            Please update Quickbooks Customer ID on
            {' '}
            <a target="_blank" href={editCompanyUrl} rel="noreferrer">
              {company}
              &#39;s edit page
            </a>
            {' '}
            so that
            invoices can be created automatically.
          </div>
          )}
      </ModalTrigger>
    );
  }

  renderActions() {
    const {
      isRep, isProjectTeamMember, isAdmin, id, consultantName, month, year,
      company, showEditButton, totals, csrfToken, statusChangeDate, consultantDueDate,
      hasQuickbooksCustomerId, reason, updatedAt, quickbooksInvoiceUrl,
      editsRequestedBy, editsRequestedOn, approvedBy, approvedOn,
    } = this.props;
    const { submissionRequested, projectedHours, status } = this.state;

    if (isRep || isProjectTeamMember) {
      return (
        <div className="actions d-flex flex-row flex-wrap flex-lg-nowrap justify-content-md-end align-items-start gap-2">
          {isRep && ['draft', 'submitted'].includes(status)
            && (
            <NotInvoicingModal
              invoiceId={id}
              consultantName={consultantName}
              notInvoicingUrl="/invoices/not_invoicing"
              csrfToken={csrfToken}
              triggerType="button"
              monthYear={`${month} ${year}`}
              reason={status === 'not_invoicing' ? reason : ''}
              redirectUrl={`/invoices/${id}`}
              triggerText="Mark as not Invoicing"
            />
            )}
          {(isProjectTeamMember || isRep) && status === 'draft'
            && (
            <div className="actions">
              <div className="body-text-2 normal-weight">
                { !isRep
                  && (
                  <Fragment>
                    <div className="muted-gray-text">
                      Wait for the submission of this draft to approve the timesheet.
                    </div>
                    <div className="muted-gray-text">
                      Draft updated
                      &nbsp;
                      {
                        dateDiffInDays(window.moment(updatedAt).toDate(), new Date())
                      }
                      &nbsp;
                      days ago.
                    </div>
                  </Fragment>
                  )}
                { !submissionRequested && isRep
                  && (
                  <div>
                    <ActionDropdown
                      id="request-submit-timesheet-button"
                      defaultLabel="Request/Submit timesheet"
                      links={[
                        ['component', this.renderRequestSubmission()],
                        ['component', this.renderSubmitTimeSheet('link')],
                      ]}
                    />
                  </div>
                  )}
                {
                  !submissionRequested && isProjectTeamMember
                  && (
                  <div>
                    { this.renderRequestSubmission('button') }
                  </div>
                  )
                }
              </div>
            </div>
            )}
          {status === 'submitted' && (
          <Fragment>
            <a className="btn btn-secondary invoice-request-edit-redirect" href={`/invoices/${id}/edit`} style={{ height: 'max-content' }}>Request Edits</a>
            <ModalTrigger
              triggerText="Approve"
              cancelButtonText="No, Don't approve yet"
              idKey="approve-invoice"
              buttonStyles="primary"
              submitButtonNode={(
                <a
                  href={`/invoices/${id}/approve`}
                  data-method="put"
                  id="approve-invoice-confirm"
                  className={`btn btn-primary ${isRep && !hasQuickbooksCustomerId ? 'disabled' : ''}`}
                >
                  Yes, Approve Invoice
                </a>
              )}
              modalSize="md"
            >
              <div className="approve-invoice-modal style-guide">
                <h2 className="mb-12">Are you sure you want to approve this timesheet?</h2>
                <p className="body-text-2 mb-15">
                  Once the timesheet is approved your company will
                  assume the payment of the billed amount in a timely maner and as indicated in the
                  contract.
                </p>
              </div>
            </ModalTrigger>
          </Fragment>
          )}
          {status === 'edits_requested'
            && (
            <div className="body-text-2 muted-gray-text normal-weight">
              {editsRequestedBy && editsRequestedOn
                ? (
                  <div>
                    {`Edits requested by ${editsRequestedBy} on `}
                    {window.moment(editsRequestedOn).format('LL')}
                  </div>
                )
                : (
                  <div>
                    {` Edits requested on ${window.moment(updatedAt).format('LL')}`}
                  </div>
                )}
              <div>
                {consultantName}
                &nbsp;
                has until
                &nbsp;
                {window.moment(consultantDueDate).format('LL')}
                &nbsp;
                to submit their changes
              </div>
            </div>
            )}
          {status === 'approved_by_rep' && isProjectTeamMember
            && (
            <div className="pt-2 me-2 body-text-2 muted-gray-text
              normal-weight"
            >
              {approvedOn && approvedBy
                ? (
                  <Fragment>
                    {`Approved by ${approvedBy}  - `}
                    {window.moment(approvedOn).format('LL')}
                    `
                  </Fragment>
                )
                : (
                  <Fragment>
                    {`Approved ${statusChangeDate}`}
                  </Fragment>
                )}
            </div>
            )}

          { (status === 'paid' || (status === 'approved_by_rep' && !(isRep || isAdmin)))
            && (
            <Fragment>
              <a href={`/invoices/${id}.pdf`} className="btn btn-secondary" target="_blank" rel="noreferrer" style={{ height: 'max-content' }}>PDF</a>
              <a href={`/invoices/${id}.csv`} className="btn" target="_blank" rel="noreferrer" style={{ height: 'max-content' }}>CSV</a>
            </Fragment>
            )}
          {(isRep && status === 'not_invoicing')
            && (
            <button
              id="btn-revert-to-draft"
              className="btn btn-secondary"
              onClick={this.handleRevertToDraft}
            >
              Return to Draft
            </button>
            )}
          { (isRep || isAdmin) && (status === 'approved_by_rep' || status === 'not_invoicing')
          && (
          <ActionDropdown
            id="approved-by-rep-actions"
            defaultLabel={(isRep && !quickbooksInvoiceUrl && status === 'approved_by_rep')
              ? this.renderPushToQuickbooks()
              : <a href={`/invoices/${id}.pdf`} className="btn btn-primary">PDF</a>}
            indicatorIcon={chevronDown}
            links={[
              ['link', 'Download PDF', `/invoices/${id}.pdf`, '_blank'],
              ['link', 'Download CSV', `/invoices/${id}.csv`, '_blank'],
              ['separator'],
              ['node', this.renderRetractButton()],
              ['node', this.renderOverridesModal()],
            ]}
          />
          )}
        </div>
      );
    }

    // consultant actions
    return (
      <div>
        <div className="actions d-flex flex-row flex-wrap gap-2">
          {status === 'draft' && totals.total === 0
            && (
            <NotInvoicingModal
              invoiceId={id}
              consultantName={consultantName}
              notInvoicingUrl="/invoices/not_invoicing"
              csrfToken={csrfToken}
              triggerType="button"
              reason={reason}
              monthYear={`${month} ${year}`}
              redirectUrl={`/invoices/${id}`}
              isConsultant
            />
            )}
          {status === 'not_invoicing' && showEditButton
            && (
            <div className="change-to-draft body-text-3">
              <div className="mb-8">
                {`Do you need to add hours for ${month} ${year}`}
              </div>
              <a href={`/invoices/${id}/draft`} data-method="put">
                <span>
                  <i className="fa fa-plus mr-5" />
                  {`Create ${month} Timesheet`}
                </span>
              </a>
            </div>
            )}
          {status === 'submitted'
            && (
            <div className="submitted-info">
              <i className="fa fa-info-circle mr-5" />
              <span className="submitted-notice medium-weight">
                {`${company} is reviewing your timesheet submitted ${statusChangeDate}.`}
              </span>
            </div>
            )}
          {(status === 'approved_by_rep' || status === 'paid') && (
          <div className="dropdown">
            <button
              className="btn btn-secondary dropdown-toggle fs-hide"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Export
            </button>
            <ul className="dropdown-menu">
              <li>
                <a target="_blank" href={`/invoices/${id}.pdf`} rel="noreferrer" className="mx-3"> PDF</a>
              </li>
              <li>
                <a target="_blank" href={`/invoices/${id}.csv`} rel="noreferrer" className="mx-3"> CSV</a>
              </li>
            </ul>
          </div>
          )}
          {showEditButton && (status === 'draft' || status === 'edits_requested')
            && <a href={`/invoices/${id}/edit`} className="btn btn-secondary edit-invoice-on-show">Edit</a>}
          { (status === 'draft' || status === 'edits_requested')
          && (projectedHours
            ? (
              <ModalTrigger
                triggerText="Submit Timesheet"
                cancelButtonText="No, Don't submit yet"
                idKey="submit-invoice"
                buttonStyles="primary"
                submitButtonNode={(
                  <a
                    href={`/invoices/${id}/submit_to_clora`}
                    data-method="put"
                    id="submit-invoice"
                    className="btn btn-primary"
                    role="button"
                  >
                    Yes, Submit to employer
                  </a>
              )}
                closeAfterSubmit
              >
                <div className="approve-invoice-modal style-guide">
                  <h2 className="mb-3">
                    Do you want to submit your
                    &nbsp;
                    {month}
                    &nbsp;
                    {year}
                    &nbsp;
                    timesheet to
                    &nbsp;
                    {company}
                    ?
                  </h2>
                  <p className="body-text-2 mb-4">
                    You cannot modify your timesheet after submitting it to the employer.
                  </p>
                </div>
              </ModalTrigger>
            )
            : (
              <a
                className="btn btn-primary"
                onClick={() => this.errorMsg(true)}
                role="button"
                tabIndex={0}
              >
                Submit Timesheet
              </a>
            )
          )}
        </div>
        {status !== 'not_invoicing'
          && (
          <div className="mt-3 body-text-2 invoice-state">
            {status !== 'submitted' && this.renderInvoiceState()}
          </div>
          )}
      </div>
    );
  }

  renderMemo() {
    const {
      isRep, isProjectTeamMember, showEditButton, consultantName,
    } = this.props;
    const {
      status, memo, isReadingMore, showReadMore,
    } = this.state;

    if (!isRep && !isProjectTeamMember && !showEditButton && (status === 'draft' || status === 'edits_requested')) {
      return (
        <div className="mt-4 body-text-2 invoice-memo">
          <div className="demibold mb-2">
            Add notes and/or instructions about your timesheet:
          </div>
          <div>
            <TextareaAutosize
              name="memo"
              className="memo"
              placeholder="Type your comments"
              value={memo}
              onChange={(e) => this.updateMemo(e.target.value)}
              rows={2}
            />
          </div>
        </div>
      );
    } if (memo) {
      return (
        <div className="mt-4 body-text-2 invoice-memo display-memo">
          <div className="demibold">
            {isRep || isProjectTeamMember ? `${consultantName}'s notes on this timesheet` : 'Your notes on this timesheet'}
          </div>
          <div
            className={`body-text-2 memo-text ${isReadingMore ? 'show' : 'overflow'}`}
            ref={(el) => { this.memoElement = el; }}
          >
            {memo}
          </div>
          {showReadMore
            && (
            <div className="align">
              <a
                className="body-text-2"
                tabIndex={0}
                role="link"
                onClick={this.readMore}
              >
                {isReadingMore ? 'Read Less' : 'Read More'}
              </a>
            </div>
            )}
        </div>
      );
    }
    return null;
  }

  renderProjectedHours() {
    const { isRep, isProjectTeamMember, showEditButton } = this.props;
    const { status, projectedHours, showErrorMsg } = this.state;

    // ability to edit
    if (!isProjectTeamMember && (!showEditButton) && (status === 'draft' || status === 'edits_requested')) {
      return (
        <div className="mt-4 body-text-2 invoice-projected-hours">
          <div className="demibold mb-2">
            How many hours do you expect to work/bill next month?
            <InfoTooltip
              triggerClassName="optional-id-tooltip-trigger"
              tooltipId="optional-id-tooltip"
              tooltipPlacement="top"
              tooltipText="Please enter your best guess, even if you don't have the exact hours.
              We use this data as an input in forecasting process and to further improve
              your future experience."
            />
            <span
              className={`required-text ${(!showErrorMsg) ? 'invoice' : 'invoice error'}`}
            >
              * Required
            </span>
          </div>

          <div className="projected-hours-container">
            <input
              className={`projected-hours-container ${(!showErrorMsg) ? 'input' : 'input error'}`}
              placeholder=""
              type="number"
              id="projectedHours"
              name="projectedHours"
              value={projectedHours !== null ? projectedHours : ''}
              onKeyDown={numericKeyFilter}
              onChange={(e) => this.updateProjectedHours(e.target.value)}
              required
            />
            <span className="ms-2">hours</span>
          </div>
          {showErrorMsg
          && <p className="invoice-error-text"> Please enter projected hours for next month to submit your timesheet</p>}
        </div>
      );
    } if (!isProjectTeamMember && (!showEditButton || isRep) && (projectedHours !== null)) {
      return (
        <div className="mt-4 body-text-2 display-proposed-hours">
          <div className="mb-2 demibold">
            Expected hours to work/bill next month
          </div>
          <div>{`${projectedHours} hours`}</div>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      invoiceNumber,
      abbreviatedMonth,
      year,
      projectName,
      consultantName,
      totals,
      company,
      feedback,
      updated,
      isRep,
      isProjectTeamMember,
      hourLimit,
      quickbooksInvoiceUrl,
      children,
      workCommitment,
      areaOfExpertises,
      tags,
      jobTitles,
      description,
      responsibilities,
      duration,
      workLocation,
      shortOfficeLocation,
      startDate,
    } = this.props;
    const { status, statusDisplay } = this.state;
    const badgeCss = isProjectTeamMember ? `${status} employer` : status;

    return (
      <div className="invoice-header card p-5 border-0 mb-3">
        <div className="card-body">
          <div className="information d-flex flex-column flex-md-row justify-content-between">
            <div className="general-info">
              <h2 className="mb-3 invoice-date-header d-flex flex-column flex-md-row flex-wrap gap-2">
                {abbreviatedMonth}
                {' '}
                {year}
                {' '}
                <span className={`badge body-text-3 ${badgeCss} d-none d-md-block`}>{statusDisplay}</span>
                {updated
                  && <span className="demibold ms-3 updated-banner">All changes saved!</span>}
                <div className="mobile-view-block body-text-2 normal-weight">
                  Invoice #
                  <span className="invoice-number">{invoiceNumber}</span>
                  <span className={`badge body-text-3 ${badgeCss} mobile-view-block`}>{statusDisplay}</span>
                  {isRep && quickbooksInvoiceUrl
                    && (
                    <span>
                      &nbsp;&#8208;
                      <a href={quickbooksInvoiceUrl} target="_blank" rel="noreferrer">
                        Quickbooks Invoice
                      </a>
                    </span>
                    )}
                </div>
                {this.renderDueDate()}
              </h2>
              <div className="body-text-2 normal-weight d-none d-md-block">
                {invoiceNumber
                  && (
                  <Fragment>
                    Invoice #
                    <span className="invoice-number">{invoiceNumber}</span>
                  </Fragment>
                  )}
                {isRep && quickbooksInvoiceUrl
                  && (
                  <span>
                    &nbsp;&#8208;
                    <a href={quickbooksInvoiceUrl} target="_blank" rel="noreferrer">
                      Quickbooks Invoice
                    </a>
                  </span>
                  )}
              </div>
            </div>
            {this.renderActions()}
          </div>
          {this.renderAmountOverrideWarning()}
          {(status === 'draft' || isRep) && hourLimit !== null && (
            <div className="mt-4 body-text-2">
              {this.renderHourLimitWarning()}
            </div>
          )}
          <div className="summary d-flex flex-column flex-lg-row justify-content-between gap-3">
            <div className="company-and-project body-text-2">
              {(isProjectTeamMember || isRep) && (
                <Fragment>
                  <div className="invoice-to demibold">
                    Consultant
                  </div>
                  <div className="consultant-name">
                    {consultantName}
                  </div>
                </Fragment>
              )}
              {!isProjectTeamMember
                && (
                <Fragment>
                  <div className="invoice-to demibold">
                    Company
                  </div>
                  <div className="company-name">
                    {company}
                  </div>
                </Fragment>
                )}
              <div className="project demibold">
                Project
              </div>
              <ProjectNameSummaryModal
                projectName={projectName}
                workCommitment={workCommitment}
                areaOfExpertises={areaOfExpertises}
                tags={tags}
                jobTitles={jobTitles}
                description={description}
                responsibilities={responsibilities}
                duration={duration}
                workLocation={workLocation}
                shortOfficeLocation={shortOfficeLocation}
                startDate={startDate}
                buttonStyles="body-text-1"
              />
            </div>
            <div className="total-invoice body-text-2">
              <div className="total-pay-header medium-weight justify-content-between">
                <div><h3>Total Invoice</h3></div>
                <div><h3>{formatCurrency(totals.total)}</h3></div>
                {totals.adjustedTotal && totals.adjustedTotal !== '$0.00'
                  ? (
                    <div>
                      <h3>
                        {`(Billing ${totals.adjustedTotal})`}
                      </h3>
                    </div>
                  ) : null}
              </div>
              <div className="consulting justify-content-between">
                <div>Consulting</div>
                <div>{formatCurrency(totals.consulting)}</div>
                {totals.adjustedConsulting && totals.adjustedConsulting !== '$0.00'
                  ? (
                    <div>
                      {`(Billing ${totals.adjustedConsulting})`}
                      `
                    </div>
                  ) : null}
              </div>
              <div className="travel justify-content-between">
                <div>Travel</div>
                <div>{formatCurrency(totals.travel)}</div>
                {totals.adjustedTravel && totals.adjustedTravel !== '$0.00'
                  ? (
                    <div>
                      {`(Billing ${totals.adjustedTravel})`}
                    </div>
                  ) : null}
              </div>
              <div className="expenses justify-content-between">
                <div>Expenses</div>
                <div>{formatCurrency(totals.expense)}</div>
              </div>
            </div>
          </div>
          {this.renderProjectedHours()}
          {this.renderMemo()}
          {feedback && (status === 'edits_requested' || (isRep && status === 'submitted'))
            && (
            <div className="invoice-feedback body-text-2">
              <div className="medium-weight mb-6">
                <i className="fa fa-edit" />
                Requested general edits:
              </div>
              <div>{feedback}</div>
            </div>
            )}
        </div>
        {children}
      </div>
    );
  }
}

InvoiceHeader.propTypes = {
  invoiceNumber: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  abbreviatedMonth: PropTypes.string.isRequired,
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  statusDisplay: PropTypes.string,
  projectName: PropTypes.string.isRequired,
  consultantName: PropTypes.string.isRequired,
  consultantDueDate: PropTypes.string,
  employerApprovalDueDate: PropTypes.string,
  memo: PropTypes.string,
  totals: PropTypes.shape({}).isRequired,
  totalHours: PropTypes.number,
  totalMinutes: PropTypes.number,
  hourLimit: PropTypes.number,
  company: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  showEditButton: PropTypes.bool,
  statusChangeDate: PropTypes.string,
  isRep: PropTypes.bool,
  isAdmin: PropTypes.bool,
  feedback: PropTypes.string,
  updated: PropTypes.bool,
  hasQuickbooksCustomerId: PropTypes.bool,
  editCompanyUrl: PropTypes.string,
  quickbooksInvoiceUrl: PropTypes.string,
  reason: PropTypes.string,
  children: PropTypes.node,
  isProjectTeamMember: PropTypes.bool,
  updatedAt: PropTypes.instanceOf(Date),
  editsRequestedBy: PropTypes.string,
  editsRequestedOn: PropTypes.instanceOf(Date),
  approvedBy: PropTypes.string,
  approvedOn: PropTypes.instanceOf(Date),
  workCommitment: PropTypes.number.isRequired,
  areaOfExpertises: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  jobTitles: PropTypes.string,
  description: PropTypes.string.isRequired,
  responsibilities: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.string.isRequired,
  workLocation: PropTypes.string.isRequired,
  shortOfficeLocation: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  showHourWarning: PropTypes.bool,
  projectedHours: PropTypes.number,
  grossRevenueOverride: PropTypes.string,
  consultantPayoutOverride: PropTypes.string,
  overrideReason: PropTypes.string,
  overrideDate: PropTypes.string,
};

InvoiceHeader.defaultProps = {
  memo: null,
  totalHours: 0,
  totalMinutes: 0,
  hourLimit: null,
  showEditButton: false,
  isRep: false,
  isProjectTeamMember: false,
  isAdmin: false,
  feedback: null,
  updated: false,
  editCompanyUrl: null,
  hasQuickbooksCustomerId: false,
  quickbooksInvoiceUrl: null,
  reason: null,
  statusDisplay: null,
  consultantDueDate: null,
  employerApprovalDueDate: null,
  company: null,
  statusChangeDate: null,
  children: null,
  updatedAt: null,
  editsRequestedBy: null,
  editsRequestedOn: null,
  approvedBy: null,
  approvedOn: null,
  areaOfExpertises: [],
  tags: [],
  jobTitles: '',
  startDate: null,
  showHourWarning: false,
  projectedHours: null,
  overrideDate: null,
  grossRevenueOverride: null,
  consultantPayoutOverride: null,
  overrideReason: null,
};

export default InvoiceHeader;
